import SelectRecherche from '../components/SelectRecherche';
import { FORMATS_FORMATION } from '../utils/formatsFormation';
import { anneeCourante } from '../utils/anneeCourante';
import '../assets/styles/DevisPanel.css';

/**
 * La relecture de ce que l'IA a lu dans un texte collé : client, séances
 * (formation, dates, lieu, formateur) et participants, tous modifiables avant
 * que le dossier soit créé. On peut aussi n'y toucher à rien.
 *
 * Contrôlé : `proposition` vient du serveur (lireProposition) et revient
 * corrigée par `onChange` ; rien n'est écrit tant que le parent n'a pas créé.
 */

const MODALITES = [
  { value: 'presentiel', label: 'Présentiel' },
  { value: 'distanciel', label: 'Distanciel' },
  { value: 'mixte', label: 'Présentiel / distanciel' },
  { value: 'inter_presentiel', label: 'Inter présentiel' },
];

export const SEANCE_VIDE = {
  formationId: null,
  formationLue: '',
  format: 'inter_presentiel',
  dureeJours: '',
  nbParticipants: '',
  montantHt: '',
  groupeLibelle: '',
  jours: [''],
  heureDebut: '09:00',
  heureFin: '17:00',
  modalite: 'presentiel',
  lieu: '',
  formateurs: '',
  formateurEmail: '',
};

export const PARTICIPANT_VIDE = { civilite: '', prenom: '', nom: '', qualite: '', societe: '', email: '' };

/** Ce qui empêcherait la création, selon l'entité — le même contrôle que le serveur, pour le dire avant le clic. */
export function manquesRelecture(proposition, entite) {
  const seances = proposition?.seances || [];
  const manques = [];
  if (entite === 'demande') {
    const c = proposition?.client || {};
    if (!String(c.societe || '').trim() && !String(c.email || '').trim() && !String(c.contact_nom || '').trim()) {
      manques.push('indiquez le client (société, e-mail ou nom de l’interlocuteur)');
    }
    if (seances.some((s) => !s.formationId)) {
      manques.push('choisissez la formation de chaque ligne, ou retirez celles qui n’en ont pas');
    }
    return manques;
  }
  if (!seances.some((s) => s.formationId)) manques.push('choisissez la formation d’au moins une séance');
  if (seances.some((s) => !s.formationId && (s.jours || []).filter(Boolean).length)) {
    manques.push('une séance qui a des dates n’a pas de formation');
  }
  if (entite !== 'contrat' && !seances.some((s) => s.formationId && (s.jours || []).filter(Boolean).length)) {
    manques.push('une date de formation est nécessaire (une journée porte la feuille et la convocation)');
  }
  if ((entite === 'convocation' || entite === 'attestation') && !(proposition?.participants || []).length) {
    manques.push(`ajoutez au moins un participant (${entite === 'convocation' ? 'une convocation' : 'une attestation'} est nominative)`);
  }
  return manques;
}

function RelectureDossier({ proposition, onChange, formations, entite = 'contrat', disabled = false }) {
  // Une demande de devis ne porte ni dates, ni lieu, ni formateur, ni participants :
  // seulement le client et les formations souhaitées (avec leurs options).
  const demande = entite === 'demande';
  const client = proposition.client || {};
  const seances = proposition.seances || [];
  const participants = proposition.participants || [];

  const majClient = (cle) => (e) => onChange({ ...proposition, client: { ...client, [cle]: e.target.value } });
  const majSeance = (i, patch) =>
    onChange({ ...proposition, seances: seances.map((s, k) => (k === i ? { ...s, ...patch } : s)) });
  const majJour = (i, j, valeur) =>
    majSeance(i, { jours: seances[i].jours.map((x, k) => (k === j ? anneeCourante(valeur) : x)) });
  const majParticipant = (i, cle) => (e) =>
    onChange({
      ...proposition,
      participants: participants.map((p, k) => (k === i ? { ...p, [cle]: e.target.value } : p)),
    });

  return (
    <div className="relecture">
      <div className="devis-form-section-title">Client</div>
      <div className="devis-form-row">
        <label className="devis-form-field">
          <span>Société</span>
          <input type="text" value={client.societe || ''} onChange={majClient('societe')} disabled={disabled} />
        </label>
        <label className="devis-form-field">
          <span>Email du client</span>
          <input type="email" value={client.email || ''} onChange={majClient('email')} disabled={disabled} />
        </label>
      </div>
      <div className="devis-form-row">
        <label className="devis-form-field">
          <span>Interlocuteur — prénom</span>
          <input type="text" value={client.contact_prenom || ''} onChange={majClient('contact_prenom')} disabled={disabled} />
        </label>
        <label className="devis-form-field">
          <span>Interlocuteur — nom</span>
          <input type="text" value={client.contact_nom || ''} onChange={majClient('contact_nom')} disabled={disabled} />
        </label>
        <label className="devis-form-field">
          <span>Téléphone</span>
          <input type="text" value={client.telephone || ''} onChange={majClient('telephone')} disabled={disabled} />
        </label>
      </div>

      <div className="devis-form-section-title">
        {demande
          ? `Formations souhaitées (${seances.length})`
          : `Séances (${seances.length}) — formation, dates, lieu et formateur`}
      </div>
      {seances.length === 0 && (
        <p className="param-hint">
          {demande ? 'Aucune formation lue : la demande sera posée sans devis.' : 'Aucune séance lue : ajoutez-en une.'}
        </p>
      )}
      {seances.map((s, i) => (
        <div className="devis-ligne-edit" key={i}>
          <div className="devis-ligne-edit-top">
            <span className="devis-ligne-numero">{i + 1}</span>
            <button
              type="button"
              className="devis-btn devis-btn-sm"
              onClick={() => onChange({ ...proposition, seances: seances.filter((_, k) => k !== i) })}
              disabled={disabled}
            >
              {demande ? 'Retirer cette formation' : 'Retirer cette séance'}
            </button>
          </div>
          <label className="devis-form-field">
            <span>
              Formation
              {!s.formationId && s.formationLue ? ` — lu : « ${s.formationLue} », non reconnu au catalogue` : ''}
            </span>
            <SelectRecherche
              value={s.formationId ? String(s.formationId) : ''}
              onChange={(e) => majSeance(i, { formationId: e.target.value ? Number(e.target.value) : null })}
              disabled={disabled}
            >
              <option value="">— Choisir la formation —</option>
              {formations.map((f) => (
                <option key={f.id} value={String(f.id)}>
                  {f.code} — {f.intitule}
                </option>
              ))}
            </SelectRecherche>
          </label>
          <div className="devis-form-row">
            <label className="devis-form-field">
              <span>Format</span>
              <SelectRecherche value={s.format} onChange={(e) => majSeance(i, { format: e.target.value })} disabled={disabled}>
                {FORMATS_FORMATION.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </SelectRecherche>
            </label>
            <label className="devis-form-field">
              <span>Durée (jours)</span>
              <input
                type="number"
                min="0.5"
                step="0.5"
                value={s.dureeJours ?? ''}
                onChange={(e) => majSeance(i, { dureeJours: e.target.value })}
                disabled={disabled}
              />
            </label>
            <label className="devis-form-field">
              <span>Participants</span>
              <input
                type="number"
                min="1"
                value={s.nbParticipants ?? ''}
                onChange={(e) => majSeance(i, { nbParticipants: e.target.value })}
                disabled={disabled}
              />
            </label>
            <label className="devis-form-field">
              <span>Montant HT (€, facultatif)</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={s.montantHt ?? ''}
                onChange={(e) => majSeance(i, { montantHt: e.target.value })}
                disabled={disabled}
              />
            </label>
          </div>

          {demande && (
            <label className="relecture-option">
              <input
                type="checkbox"
                checked={Boolean(s.estOption)}
                onChange={(e) => majSeance(i, { estOption: e.target.checked })}
                disabled={disabled}
              />
              Option (alternative proposée au client, non comptée au total)
            </label>
          )}

          {!demande && (<>
          <div className="devis-form-field">
            <span>Dates</span>
            <div className="relecture-jours">
              {s.jours.map((j, k) => (
                <span className="relecture-jour" key={k}>
                  <input
                    type="date"
                    value={j}
                    onChange={(e) => majJour(i, k, e.target.value)}
                    disabled={disabled}
                    aria-label={`Date ${k + 1} de la séance ${i + 1}`}
                  />
                  <button
                    type="button"
                    className="agenda-lien-retirer"
                    onClick={() => majSeance(i, { jours: s.jours.filter((_, m) => m !== k) })}
                    disabled={disabled}
                    aria-label="Retirer cette date"
                  >
                    ×
                  </button>
                </span>
              ))}
              <button
                type="button"
                className="devis-btn devis-btn-sm"
                onClick={() => majSeance(i, { jours: [...s.jours, ''] })}
                disabled={disabled}
              >
                + Date
              </button>
            </div>
          </div>

          <div className="devis-form-row">
            <label className="devis-form-field">
              <span>Début</span>
              <input type="time" value={s.heureDebut} onChange={(e) => majSeance(i, { heureDebut: e.target.value })} disabled={disabled} />
            </label>
            <label className="devis-form-field">
              <span>Fin</span>
              <input type="time" value={s.heureFin} onChange={(e) => majSeance(i, { heureFin: e.target.value })} disabled={disabled} />
            </label>
            <label className="devis-form-field">
              <span>Modalité</span>
              <SelectRecherche value={s.modalite} onChange={(e) => majSeance(i, { modalite: e.target.value })} disabled={disabled}>
                {MODALITES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </SelectRecherche>
            </label>
            <label className="devis-form-field" style={{ flex: 2 }}>
              <span>Lieu</span>
              <input type="text" value={s.lieu} onChange={(e) => majSeance(i, { lieu: e.target.value })} disabled={disabled} />
            </label>
          </div>
          <div className="devis-form-row">
            <label className="devis-form-field">
              <span>Formateur (nom ou prénom — sa fiche est retrouvée)</span>
              <input type="text" value={s.formateurs} onChange={(e) => majSeance(i, { formateurs: e.target.value })} disabled={disabled} />
            </label>
            <label className="devis-form-field">
              <span>Email du formateur</span>
              <input type="email" value={s.formateurEmail} onChange={(e) => majSeance(i, { formateurEmail: e.target.value })} disabled={disabled} />
            </label>
          </div>
          </>)}
        </div>
      ))}
      <button
        type="button"
        className="devis-btn devis-btn-sm"
        onClick={() => onChange({ ...proposition, seances: [...seances, { ...SEANCE_VIDE }] })}
        disabled={disabled}
      >
        {demande ? '+ Ajouter une formation' : '+ Ajouter une séance'}
      </button>

      {!demande && (<>
      <div className="devis-form-section-title">Participants ({participants.length})</div>
      {participants.length === 0 && <p className="param-hint">Aucun participant lu.</p>}
      {participants.map((p, i) => (
        <div className="devis-form-row relecture-participant" key={i}>
          <label className="devis-form-field">
            <span>Prénom</span>
            <input type="text" value={p.prenom || ''} onChange={majParticipant(i, 'prenom')} disabled={disabled} />
          </label>
          <label className="devis-form-field">
            <span>Nom</span>
            <input type="text" value={p.nom || ''} onChange={majParticipant(i, 'nom')} disabled={disabled} />
          </label>
          <label className="devis-form-field" style={{ flex: 2 }}>
            <span>Email</span>
            <input type="email" value={p.email || ''} onChange={majParticipant(i, 'email')} disabled={disabled} />
          </label>
          <label className="devis-form-field">
            <span>Qualité</span>
            <input type="text" value={p.qualite || ''} onChange={majParticipant(i, 'qualite')} disabled={disabled} />
          </label>
          <label className="devis-form-field">
            <span>Société</span>
            <input type="text" value={p.societe || ''} onChange={majParticipant(i, 'societe')} disabled={disabled} />
          </label>
          <button
            type="button"
            className="agenda-lien-retirer"
            onClick={() => onChange({ ...proposition, participants: participants.filter((_, k) => k !== i) })}
            disabled={disabled}
            aria-label={`Retirer le participant ${i + 1}`}
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        className="devis-btn devis-btn-sm"
        onClick={() => onChange({ ...proposition, participants: [...participants, { ...PARTICIPANT_VIDE }] })}
        disabled={disabled}
      >
        + Ajouter un participant
      </button>
      </>)}
    </div>
  );
}

export default RelectureDossier;
